// ═══════════════════════════════════════════════════════════
// ARAMS Dashboard — Projects Page
// ═══════════════════════════════════════════════════════════

import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { api, type Project } from '@/lib/api';
import { formatDate, getPassRate } from '@/lib/utils';
import { Card, Badge, PageHeader, Button, StatCard, Skeleton } from '@/components/ui';
import { FolderOpen, Plus, GitBranch, Users, TestTubes, Activity, ArrowRight } from 'lucide-react';

export default function ProjectsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: projects, isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.listProjects(),
  });

  if (isLoading) {
    return (
      <div className="p-8">
        <PageHeader title={t('projects.title')} />
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      </div>
    );
  }

  if (!projects || projects.length === 0) {
    // Auto-redirect to project creation when empty
    navigate('/projects/new', { replace: true });
    return null;
  }

  // Aggregate stats
  const totalRuns = projects.reduce((sum, p) => sum + (p.lastRun ? 1 : 0), 0);
  const passedRuns = projects.filter((p) => p.lastRun?.runStatus === 'passed').length;
  const totalTests = projects.reduce((sum, p) => sum + (p.lastRun?.runTotalTests || 0), 0);
  const totalRoles = projects.reduce((sum, p) => sum + (p.roleCount || 0), 0);

  return (
    <div className="p-8">
      <PageHeader
        title={t('projects.title')}
        subtitle={t('projects.subtitle')}
        actions={
          <Button onClick={() => navigate('/projects/new')}>
            <Plus className="w-4 h-4" />
            {t('projects.connectProject')}
          </Button>
        }
      />

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard
          label={t('projects.totalProjects')}
          value={projects.length}
          icon={<FolderOpen className="w-6 h-6" />}
          color="brand"
        />
        <StatCard
          label={t('projects.totalTests')}
          value={totalTests}
          icon={<TestTubes className="w-6 h-6" />}
          color="success"
        />
        <StatCard
          label={t('projects.testRoles')}
          value={totalRoles}
          icon={<Users className="w-6 h-6" />}
          color="warning"
        />
        <StatCard
          label={t('projects.passRate')}
          value={`${totalRuns > 0 ? getPassRate(passedRuns, totalRuns) : 0}%`}
          icon={<Activity className="w-6 h-6" />}
          color={passedRuns === totalRuns ? 'success' : 'danger'}
        />
      </div>

      {/* Project Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {projects.map((project, index) => (
          <ProjectCard
            key={project.projectId}
            project={project}
            index={index}
            onClick={() => navigate(`/projects/${project.projectId}`)}
          />
        ))}
      </div>
    </div>
  );
}

function ProjectCard({
  project,
  index,
  onClick,
}: {
  project: Project;
  index: number;
  onClick: () => void;
}) {
  const { t } = useTranslation();
  const lastRun = project.lastRun;
  const passRate = lastRun
    ? getPassRate(lastRun.runPassedTests || 0, lastRun.runTotalTests || 0)
    : null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.08 }}
    >
      <Card hover className="group" onClick={onClick}>
        {/* Header */}
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-500/20 to-purple-500/20 flex items-center justify-center border border-brand-500/20">
              <FolderOpen className="w-5 h-5 text-brand-400" />
            </div>
            <div>
              <h3 className="font-semibold text-surface-100 group-hover:text-brand-400 transition-colors">
                {project.projectName}
              </h3>
              <p className="text-xs text-surface-500">{project.projectSlug}</p>
            </div>
          </div>
          <Badge variant={project.projectStatus === 'active' ? 'success' : 'outline'}>
            {project.projectStatus}
          </Badge>
        </div>

        {/* Description */}
        {project.projectDescription && (
          <p className="text-xs text-surface-400 mb-4 line-clamp-2">{project.projectDescription}</p>
        )}

        {/* Stats */}
        <div className="grid grid-cols-3 gap-2 mb-4">
          <div className="text-center p-2 rounded-lg bg-surface-800/50">
            <div className="text-lg font-bold text-surface-200">{project.suiteCount || 0}</div>
            <div className="text-xs text-surface-500">{t('projects.suites')}</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-surface-800/50">
            <div className="text-lg font-bold text-surface-200">{project.roleCount || 0}</div>
            <div className="text-xs text-surface-500">{t('projects.roles')}</div>
          </div>
          <div className="text-center p-2 rounded-lg bg-surface-800/50">
            <div className="text-lg font-bold text-surface-200">
              {passRate !== null ? `${passRate}%` : '—'}
            </div>
            <div className="text-xs text-surface-500">{t('projects.passRate')}</div>
          </div>
        </div>

        {/* Last Run */}
        {lastRun ? (
          <div className="flex items-center justify-between pt-3 border-t border-surface-800/50">
            <div className="flex items-center gap-2">
              <Badge
                variant={
                  lastRun.runStatus === 'passed'
                    ? 'success'
                    : lastRun.runStatus === 'failed'
                      ? 'danger'
                      : 'info'
                }
                className="text-[10px]"
              >
                {lastRun.runStatus}
              </Badge>
              <span className="text-xs text-surface-500">
                {t('projects.passedCount', {
                  passed: lastRun.runPassedTests,
                  total: lastRun.runTotalTests,
                })}
              </span>
            </div>
            <span className="text-xs text-surface-500">
              {lastRun.runCreatedAt ? formatDate(lastRun.runCreatedAt) : ''}
            </span>
          </div>
        ) : (
          <div className="flex items-center justify-center pt-3 border-t border-surface-800/50">
            <span className="text-xs text-surface-500">{t('projects.noRunsYet')}</span>
          </div>
        )}

        {/* GitHub repo */}
        {project.projectGithubRepo && (
          <div className="flex items-center gap-1.5 mt-3 text-xs text-surface-500">
            <GitBranch className="w-3.5 h-3.5" />
            <span className="truncate">{project.projectGithubRepo}</span>
          </div>
        )}

        {/* Hover arrow */}
        <div className="absolute bottom-5 right-5 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowRight className="w-5 h-5 text-brand-400" />
        </div>
      </Card>
    </motion.div>
  );
}
